import { useState } from "react";
import { toast } from "sonner";
import { CloudDownload, CloudUpload, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/hooks/useLocale";
import type { UiStrings } from "@/lib/i18n";
import {
  GoogleCloudError,
  LARGE_FILE_WARN_BYTES,
  downloadDriveFile,
  ensureGoogleToken,
  isGoogleConfigured,
  pickDriveFiles,
  uploadToDrive,
} from "@/lib/cloud/google";

type Props = {
  provider: "google";
  disabled?: boolean;
  className?: string;
} & (
  | {
      mode: "open";
      accept?: string;
      multiple?: boolean;
      /** Receives the files brought in from Drive, to go through the same checks as a file the person chose from their device. */
      onFiles: (files: File[]) => void | Promise<void>;
    }
  | {
      mode: "save";
      fileName: string;
      /** Produces the bytes to save. Runs after the gate; applies the same trial mark a normal download gets. */
      getBlob: () => Promise<Blob>;
      /** The same usage / paywall check a normal download goes through. Returning false stops the save. */
      beforeSave?: () => Promise<boolean>;
    }
);

export function googleErrorMessage(e: unknown, s: UiStrings["googleDrive"]): string {
  const code = e instanceof GoogleCloudError ? e.code : "network";
  switch (code) {
    case "popup-blocked": return s.popupBlocked;
    case "cancelled": return s.cancelled;
    case "denied": return s.denied;
    case "script-load": return s.loadFailed;
    case "too-large": return s.tooLarge;
    case "network": return s.network;
    default: return s.api;
  }
}

/**
 * "Open from Google Drive" / "Save to Google Drive". Renders nothing at all unless the site has Google keys
 * configured, so it can ship before the keys exist. File bytes go browser <-> Google only.
 */
export function CloudPickerButton(props: Props) {
  const { strings } = useLocale();
  const s = strings.googleDrive;
  const [busy, setBusy] = useState<null | "signin" | "fetch" | "save">(null);
  if (props.provider !== "google" || !isGoogleConfigured()) return null;

  const isOpen = props.mode === "open";
  const label = isOpen ? s.openFromGoogle : s.saveToGoogle;
  const status = busy === "signin" ? s.signingIn : busy === "fetch" ? s.fetching : busy === "save" ? s.saving : "";

  const run = async () => {
    if (busy) return;
    try {
      if (props.mode === "open") {
        setBusy("signin");
        const token = await ensureGoogleToken();
        const picked = await pickDriveFiles(token, { multiple: props.multiple, accept: props.accept });
        if (picked.length === 0) return; // closed the Picker: nothing to say
        if (picked.some((p) => (p.sizeBytes ?? 0) > LARGE_FILE_WARN_BYTES)) toast.warning(s.largeFile);
        setBusy("fetch");
        const files: File[] = [];
        for (const p of picked) files.push(await downloadDriveFile(p, token));
        setBusy(null);
        await props.onFiles(files);
        return;
      }
      // Save: the same usage / paywall gate as a download comes first, so nobody signs in for a result they cannot take.
      if (props.beforeSave && !(await props.beforeSave())) return;
      setBusy("signin");
      const token = await ensureGoogleToken();
      setBusy("save");
      const blob = await props.getBlob();
      if (blob.size > LARGE_FILE_WARN_BYTES) toast.warning(s.largeFile);
      const saved = await uploadToDrive(blob, props.fileName, token);
      toast.success(s.saved, {
        description: props.fileName,
        ...(saved.webViewLink ? { action: { label: s.viewInDrive, onClick: () => window.open(saved.webViewLink, "_blank", "noopener,noreferrer") } } : {}),
      });
    } catch (e) {
      toast.error(googleErrorMessage(e, s));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className={`gap-2 ${props.className ?? ""}`}
        disabled={props.disabled || !!busy}
        aria-busy={!!busy}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); void run(); }}
        data-testid={isOpen ? "open-from-google-drive" : "save-to-google-drive"}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : isOpen ? <CloudDownload className="h-4 w-4" aria-hidden="true" /> : <CloudUpload className="h-4 w-4" aria-hidden="true" />}
        {label}
      </Button>
      <span className="sr-only" role="status" aria-live="polite">{status}</span>
    </>
  );
}
